import {
  FilterOption,
  HandleSelect,
  MetadataType,
  SetStateFunction,
  SingleExportItem,
} from "types";
import { ImageFileCard } from "../ImageFileCard";
import { State } from "redux/store";
import { connect, useDispatch } from "react-redux";
import { getSimilarityVertexCluster } from "redux/SingleDataset/selectors";
import { useLocation, useNavigate, useParams } from "react-router";
import { DATA_PAGE } from "helpers/constants/pages";
import { MouseEvent, useMemo } from "react";
import {
  setCardslistLoading,
  setNavigationCluster,
  setNavigationClusterLoader,
  setSimilarityVertex,
} from "redux/SingleDataset/actions";
import {
  PAGE_PARAMETER,
  VERTEX_CONTEXT,
  VERTEX_ID,
  VERTEX_TYPE,
} from "helpers/constants/miscellaneous";
import EnrichmentCard from "../EnrichmentCard";
import { amplitudeTrack } from "helpers/utility/amplitude";
import { AMP_DATASET_EXPLORATION_EVENT__VISUAL_SEARCH__REMOVED } from "helpers/constants/amplitudeEvents";
import { AMP_DATASET_EXPLORATION_PROPERTY__TARGET } from "helpers/constants/amplitudeProperties";
import useQueryParam from "hooks/useQueryParam";

interface VertexCardProps {
  index: number;
  vertexCluster: any;
  cardElementRef: any;
  cardContainerRef: any;
  indexOfPointerOut: number;
  showMetadataSummary: boolean;

  clusters: any;
  showDebugInfo: boolean;
  isNewFiltersPanel?: boolean;
  isCardslistLoading: boolean;
  exportData: SingleExportItem[];
  isAssetPreviewEnabled: boolean;
  datasetLabels?: FilterOption[];
  datasetUserTags?: FilterOption[];
  relevanceScores?: number[] | null;
  isNavigationClusterLoading: boolean;

  onSelect: HandleSelect;
  onIndexOfPointerOut: SetStateFunction<number>;

  //enrichment
  globalMDType: MetadataType;
  onGlobalMDTypeChange: (type: MetadataType) => void;
}

const VertexCard = ({
  index,
  globalMDType,
  vertexCluster,
  cardElementRef,
  cardContainerRef,
  indexOfPointerOut,
  showMetadataSummary,

  clusters,
  exportData,
  showDebugInfo,
  datasetLabels,
  datasetUserTags,
  relevanceScores,
  isNewFiltersPanel,
  isCardslistLoading,
  isAssetPreviewEnabled,
  isNavigationClusterLoading,

  onSelect,
  onIndexOfPointerOut,
  onGlobalMDTypeChange,
}: VertexCardProps) => {
  const { datasetId } = useParams();
  const location = useLocation();
  const searchParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search]
  );
  const vertexType = useQueryParam(VERTEX_TYPE);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const handleRemoveVertex = (e: MouseEvent) => {
    e.stopPropagation();

    //track visual search in amplitude(applied here)
    amplitudeTrack(AMP_DATASET_EXPLORATION_EVENT__VISUAL_SEARCH__REMOVED, {
      [AMP_DATASET_EXPLORATION_PROPERTY__TARGET]: vertexCluster?.type,
    });

    dispatch(setNavigationClusterLoader(true));
    dispatch(setCardslistLoading(true));
    dispatch(setNavigationCluster(null));
    dispatch(setSimilarityVertex(null));

    searchParams.delete(VERTEX_ID);
    searchParams.delete(VERTEX_TYPE);
    searchParams.delete(VERTEX_CONTEXT);
    searchParams.set(PAGE_PARAMETER, "1");
    let newPath = location.pathname.concat(`?${searchParams.toString()}`);
    navigate(newPath);
  };

  if (!vertexCluster) {
    return <noscript />;
  }
  if (
    vertexType === "UPLOAD" &&
    vertexCluster.previews &&
    vertexCluster.previews.length > 0
  ) {
    return (
      <ImageFileCard
        filename={vertexCluster.previews[0].file_name}
        imageSrc={vertexCluster.previews[0].media_uri}
        cardElementRef={cardElementRef}
        onRemoveVertex={handleRemoveVertex}
      />
    );
  } else {
    return (
      <EnrichmentCard
        page={DATA_PAGE}
        key={index}
        index={index}
        isVertex={true}
        isDataPage={true}
        isVertexCard={true}
        isClusterPage={false}
        datasetId={datasetId!}
        showSelectOption={true}
        singleData={vertexCluster}
        globalMDType={globalMDType}
        cardElementRef={cardElementRef}
        containerRef={cardContainerRef}
        clusterID={vertexCluster.cluster_id}
        indexOfPointerOut={indexOfPointerOut}
        showMetadataSummary={showMetadataSummary}
        userTagsData={vertexCluster?.user_tags || []}
        //
        clusters={clusters}
        exportData={exportData}
        showDebugInfo={showDebugInfo}
        datasetLabels={datasetLabels}
        datasetUserTags={datasetUserTags}
        relevanceScores={relevanceScores}
        isNewFiltersPanel={isNewFiltersPanel}
        isCardslistLoading={isCardslistLoading}
        isAssetPreviewEnabled={isAssetPreviewEnabled}
        isNavigationClusterLoading={isNavigationClusterLoading}
        //
        onSelect={onSelect}
        onRemoveVertex={handleRemoveVertex}
        onIndexOfPointerOut={onIndexOfPointerOut}
        onGlobalMDTypeChange={onGlobalMDTypeChange}
      />
    );
  }
};

const mapStateToProps = (state: State) => {
  return {
    vertexCluster: getSimilarityVertexCluster(state),
  };
};
export default connect(mapStateToProps)(VertexCard);
